/**
 * Ordering domain events.
 * Tracked in: apps/ordering, apps/checkout, apps/menu
 *
 * Value: Conversion funnels, cart abandonment recovery,
 * order volume metrics, diner behavior insights.
 */

import { defineEvent } from './types';

// --- Cart ---

export const cartViewed = defineEvent<'cart_viewed', {
    cartId: string;
    restaurantId: string;
    itemCount: number;
    subtotal: number;
}>('cart_viewed');

export const itemAddedToCart = defineEvent<'item_added_to_cart', {
    cartId: string;
    restaurantId: string;
    itemId: string;
    itemName: string;
    quantity: number;
    price: number;
    modifiers?: string[];
}>('item_added_to_cart');

export const itemRemovedFromCart = defineEvent<'item_removed_from_cart', {
    cartId: string;
    restaurantId: string;
    itemId: string;
    itemName: string;
    quantity: number;
}>('item_removed_from_cart');

export const cartAbandoned = defineEvent<'cart_abandoned', {
    cartId: string;
    restaurantId: string;
    itemCount: number;
    subtotal: number;
    lastStep: 'menu' | 'cart' | 'checkout' | 'payment';
    durationSeconds: number;
}>('cart_abandoned');

// --- Checkout ---

export const checkoutStarted = defineEvent<'checkout_started', {
    cartId: string;
    restaurantId: string;
    itemCount: number;
    subtotal: number;
    orderType: string;
}>('checkout_started');

export const checkoutStepCompleted = defineEvent<'checkout_step_completed', {
    cartId: string;
    restaurantId: string;
    step: 'contact' | 'delivery_address' | 'tip' | 'payment_method' | 'review';
    stepDurationSeconds: number;
}>('checkout_step_completed');

export const checkoutAbandoned = defineEvent<'checkout_abandoned', {
    cartId: string;
    restaurantId: string;
    lastStep: string;
    subtotal: number;
    durationSeconds: number;
}>('checkout_abandoned');

// --- Order ---

export const orderPlaced = defineEvent<'order_placed', {
    orderId: string;
    restaurantId: string;
    tenantId: string;
    orderType: string;
    itemCount: number;
    subtotal: number;
    tax: number;
    tip: number;
    total: number;
    paymentMethod: string;
    isFirstOrder: boolean;
}>('order_placed');

export const orderFailed = defineEvent<'order_failed', {
    restaurantId: string;
    tenantId: string;
    errorCode: string;
    errorMessage: string;
    orderType: string;
    subtotal: number;
}>('order_failed');

// --- Menu browsing ---

export const menuViewed = defineEvent<'menu_viewed', {
    restaurantId: string;
    menuGroupId?: string;
    source: 'direct' | 'qr_code' | 'link' | 'search';
    /**
     * WHICH surface rendered the menu. `standard` is the ordering/restaurant
     * menu with a cart; `table` is the guest table page reached by scanning the
     * code on a table, which is read-only until piece 4 lands.
     *
     * It is a separate dimension from `source` on purpose: a table scan is
     * `source: 'qr_code'` and so is a printed takeaway flyer, and the two
     * convert nothing alike. Absent means `standard` for every event emitted
     * before this existed.
     */
    mode?: 'standard' | 'table';
}>('menu_viewed');

export const menuItemViewed = defineEvent<'menu_item_viewed', {
    restaurantId: string;
    itemId: string;
    itemName: string;
    price: number;
    menuGroupId?: string;
}>('menu_item_viewed');

export const menuSearched = defineEvent<'menu_searched', {
    restaurantId: string;
    query: string;
    resultCount: number;
}>('menu_searched');
